import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getInventoryItems,
  getReminders,
  updateInventoryItem,
  updateReminder,
} from "../services/settingsService";
import { validateReminderDays, validateThreshold } from "../validations/reminderValidation";

const normalizeReminders = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const normalizeInventoryItems = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const useReminderSettings = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedItemUnit, setSelectedItemUnit] = useState("Units");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [isItemActive, setIsItemActive] = useState(true);
  const [isInventorySaving, setIsInventorySaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [remindersRes, inventoryRes] = await Promise.all([
        getReminders(),
        getInventoryItems(),
      ]);
      setReminders(normalizeReminders(remindersRes));
      setInventoryItems(normalizeInventoryItems(inventoryRes));
    } catch {
      toast.error("Failed to load reminder settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdate = async (id, data) => {
    setSaving(true);
    try {
      await updateReminder(id, data);
      setReminders((prev) => prev.map((r) => (r._id === id ? { ...r, ...data } : r)));
      toast.success("Reminder updated");
    } catch {
      toast.error("Failed to update reminder");
    } finally {
      setSaving(false);
    }
  };

  const updateDays = async (id, days) => {
    const parsed = validateReminderDays(days);
    if (!parsed.valid) {
      toast.error("Days must be at least 1");
      return;
    }
    await handleUpdate(id, { days: parsed.value });
  };

  const toggleActive = async (reminder) => {
    await handleUpdate(reminder._id, { active: !reminder.active });
  };

  const onSelectItem = (id) => {
    setSelectedItemId(id);
    const item = inventoryItems.find((entry) => entry._id === id);
    if (!item) {
      setLowStockThreshold("");
      setIsItemActive(true);
      setSelectedItemUnit("Units");
      return;
    }
    setLowStockThreshold(item.minQty ?? 0);
    setIsItemActive(item.active !== false);
    setSelectedItemUnit(item?.units?.title || "Units");
  };

  const saveInventoryThreshold = async () => {
    if (!selectedItemId) {
      toast.error("Please select an item");
      return;
    }

    const parsed = validateThreshold(lowStockThreshold);
    if (!parsed.valid) {
      toast.error("Threshold must be a valid number");
      return;
    }

    const item = inventoryItems.find((entry) => entry._id === selectedItemId);
    if (!item) return;

    const payload = {
      settingType: "item",
      type: item.type,
      category: item.category?._id || item.category,
      brand: item.brand?._id || item.brand,
      itemName: item.itemName,
      itemCode: item.itemCode,
      units: item.units?._id || item.units,
      minQty: parsed.value,
      active: isItemActive,
    };

    setIsInventorySaving(true);
    try {
      await updateInventoryItem(selectedItemId, payload);
      setInventoryItems((prev) =>
        prev.map((entry) =>
          entry._id === selectedItemId
            ? { ...entry, minQty: parsed.value, active: isItemActive }
            : entry,
        ),
      );
      toast.success("Low stock configuration updated");
    } catch {
      toast.error("Failed to update threshold");
    } finally {
      setIsInventorySaving(false);
    }
  };

  return {
    reminders,
    setReminders,
    loading,
    saving,
    toggleActive,
    updateDays,
    inventoryItems,
    selectedItemId,
    setSelectedItemId: onSelectItem,
    selectedItemUnit,
    lowStockThreshold,
    setLowStockThreshold,
    isItemActive,
    setIsItemActive,
    saveInventoryThreshold,
    isInventorySaving,
  };
};




